// Shared Navigation Libraries — mirrors vortex-api services/navLibraryService.ts.
import type { NavType, NavStats } from './nav';

export type LibraryVisibility = 'listed' | 'unlisted';
/** request = owner approves each request · open = requests are approved automatically */
export type LibraryAccessMode = 'request' | 'open';
export type MemberStatus = 'requested' | 'approved' | 'declined' | 'revoked';

export interface NavLibrary {
  id: string;
  ownerUserId: string;
  ownerName: string | null;
  name: string;
  description: string | null;
  region: string | null;
  visibility: LibraryVisibility;
  accessMode: LibraryAccessMode;
  priceCents: number | null;
  currency: string | null;
  /** Set while Vortex has taken the library down (only its curator still sees it). */
  takenDownAt?: string | null;
  itemCount: number;
  memberCount: number;
  pendingCount: number;
  ratingAvg: number | null;
  ratingCount: number;
  copyCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export type ExploreSort = 'top' | 'new' | 'popular';

export const RATING_TAGS = ['accurate', 'well_described', 'kept_up_to_date', 'saved_time'] as const;
export type RatingTag = (typeof RATING_TAGS)[number];
export const RATING_TAG_LABEL: Record<RatingTag, string> = {
  accurate: 'Accurate', well_described: 'Well described', kept_up_to_date: 'Kept up to date', saved_time: 'Saved me time',
};

export interface LibraryRating {
  id: string;
  libraryId: string;
  userId: string;
  userName: string | null;
  stars: number;
  tags: string[];
  text: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface RatingSummary {
  count: number;
  avg: number | null;
  tagCounts: Record<RatingTag, number>;
  recent: LibraryRating[];
}

export interface CuratorProfile {
  userId: string;
  name: string | null;
  libraries: NavLibrary[];
  totals: { libraries: number; items: number; members: number; copies: number; ratingAvg: number | null; ratingCount: number };
}

export interface ExploreLibrary extends NavLibrary {
  membership: MemberStatus | null;
  mine: boolean;
}

export interface SubscribedLibrary extends NavLibrary {
  membership: MemberStatus;
}

export interface LibraryItem {
  elementId: string;
  type: NavType;
  name: string;
  description: string | null;
  tags: string[];
  stats: NavStats;
  /** empty until you can browse the library */
  points: [number, number][];
  updatedAt: string | null;
  addedAt: string | null;
  /** your copy of this item, if any; stale = curator edited it since you copied */
  copy: { id: string; stale: boolean } | null;
}

export type LibraryRole = 'owner' | 'member' | 'requested' | 'declined' | 'revoked' | 'none';

export interface LibraryDetail {
  library: NavLibrary;
  role: LibraryRole;
  canBrowse: boolean;
  items: LibraryItem[];
  ratings: RatingSummary;
  myRating: LibraryRating | null;
  /** you've copied ≥1 item and aren't the owner */
  canRate: boolean;
}

export interface LibraryMember {
  id: string;
  libraryId: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  status: MemberStatus;
  message: string | null;
  requestedAt: string | null;
  decidedAt: string | null;
}

export interface LibraryInput {
  name: string;
  description?: string | null;
  region?: string | null;
  visibility?: LibraryVisibility;
  access_mode?: LibraryAccessMode;
}

// ---- moderation ----
export type ReportReason = 'inaccurate' | 'unsafe' | 'copyright' | 'inappropriate' | 'other';
export type ReportStatus = 'open' | 'resolved' | 'dismissed';
export const REPORT_REASONS: ReportReason[] = ['inaccurate', 'unsafe', 'copyright', 'inappropriate', 'other'];
export const REPORT_REASON_LABEL: Record<ReportReason, string> = {
  inaccurate: 'Inaccurate or out of date',
  unsafe: 'Unsafe to follow',
  copyright: 'Copyright / not theirs to share',
  inappropriate: 'Inappropriate content',
  other: 'Something else',
};

export interface LibraryReport {
  id: string;
  libraryId: string;
  libraryName: string | null;
  libraryOwnerName: string | null;
  libraryVisibility: string | null;
  /** Set while the library is taken down (Navigation Library and shared document libraries). */
  libraryTakenDownAt?: string | null;
  reporterUserId: string;
  reporterName: string | null;
  reporterEmail: string | null;
  reason: ReportReason;
  details: string | null;
  status: ReportStatus;
  resolutionNote: string | null;
  resolvedByUserId: string | null;
  createdAt: string | null;
  resolvedAt: string | null;
}
